import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listRows } from '@/api/resources';
import { AddButton, LiveScreen, QueryState, StatusBadge, StatusPill, formatDate, formatDateTime, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { RecordDrawer } from '@/components/RecordDrawer';
import { StatCards, applyStat } from '@/components/StatCards';
import { ValidityBadge } from '@/components/ValidityBadge';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { languageName } from '@/lib/languages';
import { scopeLabel } from '@/lib/plotScopes';
import { NewPlotConsent, PlotConsentDrawer, PlotConsentList } from '@/screens/parts/PlotConsent';
import { PlotChecklist } from '@/screens/parts/PlotChecklist';
import { LinkedDocumentLink } from '@/components/LinkedDocumentLink';
import type { ConsentRow } from '@/types/records';

const STATE: Record<string, string> = { pending: 'Waiting for the farmer', generated: 'Waiting for the farmer', acknowledged: 'Consent given', revoked: 'Withdrawn', expired: 'Expired' };
const KIND: Record<string, string> = { plot_enrolment: 'Programme', harvest_sale: 'Sale', family_consent: 'Family members',
  sharecrop_consent: 'Sharecropping', representative: 'Representative', consent_amendment: 'Amendment' };

interface ChecklistRow {
  id: string;
  plot_code: string | null; plot_no: string | null; plot_name: string | null; plot_status: string;
  farmer_id: string; farmer_code: string | null; farmer_name: string | null; village_name: string | null;
  kyc_ready: boolean; kyc_detail: string;
  records_ready: boolean; records_detail: string;
  programme_ready: boolean; programme_detail: string; programme_consent_id: string | null;
  sale_ready: boolean; sale_detail: string; sale_consent_id: string | null;
  rights_ready: boolean | null; rights_detail: string | null; rights_consent_id: string | null;
  items_ready: number; items_total: number; is_ready: boolean;
}

function Item({ ready, detail }: { ready: boolean; detail: string }) {
  return (
    <span className="cell-stack">
      <StatusBadge label={ready ? 'Done' : 'To do'} tone={ready ? 'green' : 'amber'} />
      <small className="muted">{detail}</small>
    </span>
  );
}

/**
 * Every plot's consents, on a page of its own (owner, 2026-09-27: "We generally obtain consent from
 * farmers per plot"). Two views:
 *  - Checklist: a row per plot - every plot has one from the day it is recorded, and its programme
 *    and sale consents are opened when it is approved (owner, 2026-09-28: "gets autocreate a row per
 *    plot") - with farmer KYC, plot records, programme consent and sale consent in that order.
 *    Harvest and sale from the plot wait until all four are done.
 *  - Consents: each consent itself - programme and sale consents per plot, and the farmer's
 *    representative consents ("Farmer Representative Consent also in the list").
 */
export default function PlotConsentsScreen() {
  const { profile } = useAuth();
  const mayIssue = can(profile, 'party.consent', 'create');
  const [tab, setTab] = useState<'checklist' | 'consents'>('checklist');
  const [stat, setStat] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [openPlot, setOpenPlot] = useState<ChecklistRow | null>(null);
  const [open, setOpen] = useState<ConsentRow | null>(null);
  const [adding, setAdding] = useState(false);
  // Opened from a plot or a farmer (owner, 2026-09-28: "bring all at consent page"): ?plot=CODE opens
  // that plot's checklist, ?consent=ID that consent.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const consentId = params.get('consent');
    const plotCode = params.get('plot');
    if (consentId) {
      void listRows<ConsentRow>('party_consents', { filters: { id: consentId } }).then((rows) => { if (rows[0]) setOpen(rows[0]); });
    } else if (plotCode) {
      setTab('checklist');
      void listRows<ChecklistRow>('plot_checklist', { filters: { plot_code: plotCode } }).then((rows) => { if (rows[0]) setOpenPlot(rows[0]); });
    }
    if (consentId || plotCode) setParams({}, { replace: true });
  }, [params, setParams]);

  const checklist = useRows<ChecklistRow>('plot_checklist', {
    search: search ? { columns: ['farmer_name', 'farmer_code', 'plot_code'], term: search } : undefined,
    orderBy: { column: 'created_at', ascending: false },
  }, tab === 'checklist');
  const consentQuery = (type: string) => ({
    filters: { consent_type: type },
    search: search ? { columns: ['farmer_name', 'farmer_code', 'plot_code'], term: search } : undefined,
    orderBy: { column: 'created_at', ascending: false },
  });
  const programme = useRows<ConsentRow>('party_consents', consentQuery('plot_enrolment'), tab === 'consents');
  const sale = useRows<ConsentRow>('party_consents', consentQuery('harvest_sale'), tab === 'consents');
  const family = useRows<ConsentRow>('party_consents', consentQuery('family_consent'), tab === 'consents');
  const sharecrop = useRows<ConsentRow>('party_consents', consentQuery('sharecrop_consent'), tab === 'consents');
  const representative = useRows<ConsentRow>('party_consents', consentQuery('representative'), tab === 'consents');
  const amendment = useRows<ConsentRow>('party_consents', consentQuery('consent_amendment'), tab === 'consents');
  const consents = useMemo(() => [
    ...(programme.data ?? []).filter((row) => row.plot_id),
    ...(sale.data ?? []),
    ...(family.data ?? []),
    ...(sharecrop.data ?? []),
    ...(representative.data ?? []),
    ...(amendment.data ?? []),
  ].sort((a, b) => String((b as { created_at?: string }).created_at ?? '').localeCompare(String((a as { created_at?: string }).created_at ?? ''))), [programme.data, sale.data, family.data, sharecrop.data, representative.data, amendment.data]);

  const inAYear = new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const PLOT_STATS = useMemo(() => [
    { key: 'all', label: 'Plots', tone: 'neutral' as const, match: () => true },
    { key: 'ready', label: 'Ready for harvest and sale', tone: 'ok' as const, match: (row: ChecklistRow) => row.is_ready },
    { key: 'kyc', label: 'Farmer KYC to do', tone: 'warn' as const, match: (row: ChecklistRow) => !row.kyc_ready },
    { key: 'records', label: 'Plot records to do', tone: 'warn' as const, match: (row: ChecklistRow) => !row.records_ready },
    { key: 'programme', label: 'Programme consent to do', tone: 'warn' as const, match: (row: ChecklistRow) => !row.programme_ready },
    { key: 'sale', label: 'Sale consent to do', tone: 'warn' as const, match: (row: ChecklistRow) => !row.sale_ready },
    { key: 'rights', label: 'Family / sharecropping to do', tone: 'warn' as const, match: (row: ChecklistRow) => row.rights_ready === false },
  ], []);
  const CONSENT_STATS = useMemo(() => [
    { key: 'all', label: 'Consents', tone: 'neutral' as const, match: () => true },
    { key: 'programme', label: 'Programme', tone: 'info' as const, match: (row: ConsentRow) => row.consent_type === 'plot_enrolment' },
    { key: 'sale', label: 'Sale', tone: 'info' as const, match: (row: ConsentRow) => row.consent_type === 'harvest_sale' },
    { key: 'rights', label: 'Family / sharecropping', tone: 'info' as const, match: (row: ConsentRow) => ['family_consent', 'sharecrop_consent'].includes(row.consent_type) },
    { key: 'representative', label: 'Representative', tone: 'info' as const, match: (row: ConsentRow) => row.consent_type === 'representative' },
    { key: 'amendment', label: 'Amendments', tone: 'info' as const, match: (row: ConsentRow) => row.consent_type === 'consent_amendment' },
    { key: 'waiting', label: 'Waiting for the farmer', tone: 'warn' as const, match: (row: ConsentRow) => ['pending', 'generated'].includes(row.status) },
    { key: 'given', label: 'Consent given', tone: 'ok' as const, match: (row: ConsentRow) => row.status === 'acknowledged' },
    { key: 'due', label: 'Due within a year', tone: 'warn' as const, match: (row: ConsentRow) => row.status === 'acknowledged' && Boolean(row.valid_to) && row.valid_to! <= inAYear && row.valid_to! >= today },
    { key: 'withdrawn', label: 'Withdrawn / revoked', tone: 'error' as const, match: (row: ConsentRow) => Boolean(row.revoked_at) || ['revoked', 'withdrawn'].includes(row.status) },
  ], [inAYear, today]);
  const plotsVisible = useMemo(() => applyStat(checklist.data ?? [], PLOT_STATS, stat), [checklist.data, PLOT_STATS, stat]);
  const consentsVisible = useMemo(() => applyStat(consents, CONSENT_STATS, stat), [consents, CONSENT_STATS, stat]);

  const farmerCell = (row: { farmer_name?: string | null; farmer_code?: string | null; village_name?: string | null }) => (
    <span className="cell-stack"><strong>{row.farmer_name ?? '--'}</strong><small className="muted">{[row.farmer_code, row.village_name].filter(Boolean).join(' · ')}</small></span>
  );
  const plotColumns: Array<Column<ChecklistRow>> = [
    { key: 'farmer_name', label: 'Farmer', render: farmerCell },
    { key: 'plot_code', label: 'Plot', render: (row) => (
      <span className="cell-stack">{[row.plot_code, row.plot_no].filter(Boolean).join(' · ') || '--'}<small className="muted">{row.plot_status === 'verified' ? 'Approved' : 'Not approved yet'}</small></span>
    ) },
    { key: 'kyc_ready', label: '1. Farmer KYC', render: (row) => <Item detail={row.kyc_detail} ready={row.kyc_ready} /> },
    { key: 'records_ready', label: '2. Plot records', render: (row) => <Item detail={row.records_detail} ready={row.records_ready} /> },
    // Only a jointly held, inherited or sharecropped plot has this item (migration 740000).
    { key: 'rights_ready', label: 'Family / sharecropping', render: (row) => (row.rights_detail
      ? <Item detail={row.rights_detail} ready={Boolean(row.rights_ready)} /> : <span className="muted">Not needed</span>) },
    { key: 'programme_ready', label: '3. Programme consent', render: (row) => <Item detail={row.programme_detail} ready={row.programme_ready} /> },
    { key: 'sale_ready', label: '4. Sale consent', render: (row) => <Item detail={row.sale_detail} ready={row.sale_ready} /> },
    { key: 'items_ready', label: 'Harvest and sale', render: (row) => (
      <StatusBadge label={row.is_ready ? 'Ready' : `Blocked · ${row.items_ready} of ${row.items_total}`} tone={row.is_ready ? 'green' : 'amber'} />
    ) },
  ];
  const consentColumns: Array<Column<ConsentRow>> = [
    { key: 'farmer_name', label: 'Farmer', render: farmerCell },
    { key: 'consent_type', label: 'Consent', render: (row) => KIND[row.consent_type] ?? row.consent_type },
    { key: 'plot_code', label: 'Plot', render: (row) => [row.plot_code, row.plot_no].filter(Boolean).join(' · ') || (row.consent_type === 'representative' ? 'Farmer\'s plots' : '--') },
    { key: 'scope_codes', label: 'Standards', render: (row) => (row.consent_type === 'plot_enrolment' ? (row.scope_codes ?? []).map(scopeLabel).join(', ') || 'Membership' : '--') },
    { key: 'language_code', label: 'Languages', render: (row) => (row.language_code && row.language_code !== 'en' && row.consent_type === 'plot_enrolment'
      ? `EN + ${languageName(row.language_code)}` : languageName(row.language_code)) },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={STATE[row.status] ?? row.status} /> },
    { key: 'document_sent_at', label: 'PDF sent', render: (row) => (row.document_sent_at
      ? <span className="cell-stack">{formatDate(row.document_sent_at)}<small className="muted">{(row.document_sent_via ?? []).join(', ')}</small></span> : '--') },
    { key: 'acknowledged_at', label: 'Given', render: (row) => (row.acknowledged_at ? formatDateTime(row.acknowledged_at) : '--') },
    { key: 'valid_to', label: 'Valid', render: (row) => (row.status === 'revoked' ? <span className="muted">Ended {row.revoked_at ? formatDate(row.revoked_at) : ''}</span>
      : row.valid_to ? <ValidityBadge compact from={row.valid_from} to={row.valid_to} /> : '--') },
    { key: 'draft_doc', label: 'Draft', render: (row) => <LinkedDocumentLink entityId={row.id} entityTable="party.party_consents" label="Draft" linkType="generated" /> },
    { key: 'signed_doc', label: 'Consented', render: (row) => <LinkedDocumentLink entityId={row.id} entityTable="party.party_consents" label="Signed" linkType="primary" /> },
  ];
  const consentsQuery = [programme, sale, family, sharecrop, representative].find((query) => query.isError) ?? programme;

  return (
    <LiveScreen
      actions={<AddButton disabled={!mayIssue} label="New plot consent" onClick={() => setAdding(true)}
                          title={mayIssue ? undefined : 'Your role cannot take consents.'} />}
      subtitle="A row per plot: farmer KYC, plot records, the family's or sharecropping consent where the tenure needs it, programme consent and sale consent. Harvest and sale from a plot wait until all are done."
      title="Plot consents"
    >
      <div className="tab-row" role="tablist">
        <button aria-selected={tab === 'checklist'} className={tab === 'checklist' ? 'active' : ''} onClick={() => { setTab('checklist'); setStat(null); }} role="tab" type="button">Checklist per plot</button>
        <button aria-selected={tab === 'consents'} className={tab === 'consents' ? 'active' : ''} onClick={() => { setTab('consents'); setStat(null); }} role="tab" type="button">Consents</button>
      </div>

      {tab === 'checklist' ? (<>
        <StatCards active={stat} onSelect={setStat} rows={checklist.data ?? []} stats={PLOT_STATS} />
        <QueryState empty="No plot is on record yet." query={checklist}>
          <DataGrid columns={plotColumns} exportName="plot-checklist" onRowClick={setOpenPlot} onSearchChange={setSearch}
                    recordNoun="plot" rows={plotsVisible} screenKey="party.consent" search={search} searchPlaceholder="Farmer or plot"
                    selectedId={openPlot?.id} />
        </QueryState>
      </>) : (<>
        <StatCards active={stat} onSelect={setStat} rows={consents} stats={CONSENT_STATS} />
        <QueryState empty="No consent has been taken yet. Start one with New plot consent." query={consentsQuery}>
          <DataGrid columns={consentColumns} exportName="plot-consents" onRowClick={setOpen} onSearchChange={setSearch}
                    recordNoun="consent" rows={consentsVisible} screenKey="party.consent" search={search} searchPlaceholder="Farmer or plot"
                    selectedId={open?.id} />
        </QueryState>
      </>)}

      {openPlot && (
        <RecordDrawer onClose={() => setOpenPlot(null)} open record={null}
                      subtitle={[openPlot.farmer_name, openPlot.farmer_code, openPlot.village_name].filter(Boolean).join(' · ')}
                      title={[openPlot.plot_code, openPlot.plot_no].filter(Boolean).join(' · ') || 'Plot'}>
          <PlotChecklist farmerCode={openPlot.farmer_code} plotCode={openPlot.plot_code} plotId={openPlot.id} />
          <PlotConsentList plotId={openPlot.id} />
        </RecordDrawer>
      )}
      {adding && <NewPlotConsent onClose={() => setAdding(false)} onCreated={(id) => { setAdding(false); setTab('consents'); setOpen({ id, consent_type: 'plot_enrolment' } as ConsentRow); }} />}
      {open && <PlotConsentDrawer consentId={open.id} onClose={() => setOpen(null)} />}
    </LiveScreen>
  );
}
